import React from 'react';
import { Link } from 'gatsby';
import styled from '@emotion/styled';
import { useLang } from '../../context/LanguageContext';
import { t } from '../../translations';

const Section = styled.section`
  background: #FFFFFF;
  padding: 100px 50px;

  @media (max-width: 640px) {
    padding: 64px 20px;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  margin-bottom: 64px;
  text-align: center;

  @media (max-width: 640px) {
    text-align: left;
  }
`;

const Label = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #D97757;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0 0 16px;
`;

const Title = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(28px, 4vw, 48px);
  font-weight: 700;
  color: #0A0A0A;
  letter-spacing: -1px;
  line-height: 1.15;
  max-width: 560px;
  margin: 0 auto 16px;

  @media (max-width: 640px) {
    margin: 0 0 16px;
  }
`;

const Subtitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  color: #666;
  line-height: 1.7;
  max-width: 480px;
  margin: 0 auto;

  @media (max-width: 640px) {
    margin: 0;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  display: block;
  text-decoration: none;
  background: #FFFFFF;
  border: 1px solid #EBEBEB;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: border-color 250ms ease, transform 250ms ease, box-shadow 250ms ease;

  &:hover {
    border-color: #D8D8D8;
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.1);
  }
`;

const CardDiv = styled.div`
  display: block;
  text-decoration: none;
  background: #FFFFFF;
  border: 1px solid #EBEBEB;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: border-color 250ms ease, transform 250ms ease, box-shadow 250ms ease;

  &:hover {
    border-color: #D8D8D8;
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.1);
  }
`;

const ImageWrap = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 4 / 3;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 400ms ease;

  ${ImageWrap}:hover & {
    transform: scale(1.04);
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 14px;
  left: 14px;
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0,0,0,0.1);
  border-radius: 100px;
  font-family: 'Poppins', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #111;
  letter-spacing: 0.5px;
`;

const CardBody = styled.div`
  padding: 24px;
`;

const CardTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 17px;
  font-weight: 600;
  color: #0A0A0A;
  margin: 0 0 8px;
`;

const CardDesc = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #666;
  line-height: 1.65;
  margin: 0;
`;

const projectMeta = [
  { to: '/project/cityeat', image: 'https://uploads-ssl.webflow.com/5ff1d3813d451abf8cb1fd59/60212c661388a10a700fb79c_02.jpg', isLink: true },
  { to: '/project/armada-golf-club', image: '/armada/mockup1.jpg', isLink: true },
  { to: '/work/inwestycje-deweloperskie', image: 'https://uploads-ssl.webflow.com/5ff1d3813d451abf8cb1fd59/6021551942193722d257781f_02.jpg', isLink: true },
];

const Portfolio = () => {
  const { lang } = useLang();
  const tr = t[lang].portfolio;

  return (
    <Section>
      <Container>
        <SectionHeader>
          <Label>{tr.label}</Label>
          <Title>{tr.title}</Title>
          <Subtitle>{tr.subtitle}</Subtitle>
        </SectionHeader>

        <Grid>
          {tr.projects.map((project, i) => {
            const meta = projectMeta[i];
            const content = (
              <>
                <ImageWrap>
                  <CardImage src={meta.image} alt={project.title} loading="lazy" />
                  <Badge>{project.badge}</Badge>
                </ImageWrap>
                <CardBody>
                  <CardTitle>{project.title}</CardTitle>
                  <CardDesc>{project.desc}</CardDesc>
                </CardBody>
              </>
            );

            return meta.isLink ? (
              <Card key={project.title} to={meta.to}>{content}</Card>
            ) : (
              <CardDiv key={project.title}>{content}</CardDiv>
            );
          })}
        </Grid>
      </Container>
    </Section>
  );
};

export default Portfolio;
